import { zodResolver } from '@hookform/resolvers/zod';
import { adminCreateUserSchema, adminUserSchema, paginated } from '@hearth/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, Plus, Search, ShieldCheck, Users as UsersIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { FormField } from '@/components/form-field';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiRequest } from '@/lib/api';
import { applyServerErrors } from '@/lib/forms';
import { timeAgo } from '@/lib/format';

const FILTERS = ['all', 'admins', 'suspended'] as const;
type Filter = (typeof FILTERS)[number];

export const Route = createFileRoute('/_app/admin/users/')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { page?: number; search?: string; filter?: Filter } => ({
    page: Number(s.page) > 1 ? Number(s.page) : undefined,
    search: typeof s.search === 'string' && s.search ? s.search : undefined,
    filter:
      FILTERS.includes(s.filter as Filter) && s.filter !== 'all' ? (s.filter as Filter) : undefined,
  }),
  component: UsersPage,
});

function UsersPage() {
  const { page = 1, search = '', filter = 'all' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [term, setTerm] = useState(search);
  const [creating, setCreating] = useState(false);

  // Debounce the search box into the URL.
  useEffect(() => {
    if (term === search) return;
    const t = setTimeout(
      () =>
        void navigate({ search: (s) => ({ ...s, search: term || undefined, page: undefined }) }),
      300,
    );
    return () => clearTimeout(t);
  }, [term, search, navigate]);

  const query = useQuery({
    queryKey: ['admin', 'users', { page, search, filter }],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ page: String(page), perPage: '25', filter });
      if (search) params.set('search', search);
      return apiRequest(`/api/application/users?${params}`, {
        schema: paginated(adminUserSchema),
        signal,
      });
    },
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Users"
        icon={UsersIcon}
        description="Everyone with an account on this panel."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> New user
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
          <Input
            className="pl-8"
            placeholder="Search email or username"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            aria-label="Search users"
          />
        </div>
        <Tabs
          value={filter}
          onValueChange={(v) =>
            void navigate({
              search: (s) => ({
                ...s,
                filter: v === 'all' ? undefined : (v as Filter),
                page: undefined,
              }),
            })
          }
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="admins">Admins</TabsTrigger>
            <TabsTrigger value="suspended">Suspended</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <QueryState query={query}>
        {(data) => (
          <div className="space-y-4">
            <div className="table-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground py-8 text-center">
                        No users match.
                      </TableCell>
                    </TableRow>
                  )}
                  {data.data.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <Link
                          to="/admin/users/$userId"
                          params={{ userId: String(u.id) }}
                          className="font-medium hover:underline"
                        >
                          {u.username}
                        </Link>
                        <div className="text-muted-foreground text-xs">{u.email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {u.isAdmin && <Badge>Admin</Badge>}
                          {u.suspended && <Badge variant="destructive">Suspended</Badge>}
                          {u.twoFactorEnabled && (
                            <Badge variant="secondary">
                              <ShieldCheck /> 2FA
                            </Badge>
                          )}
                          {!u.emailVerified && <Badge variant="outline">Unverified</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-right text-xs">
                        {timeAgo(u.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pagination
              {...data.meta}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p }) })}
            />
          </div>
        )}
      </QueryState>

      {creating && <CreateUserDialog onClose={() => setCreating(false)} />}
    </>
  );
}

type CreateValues = z.input<typeof adminCreateUserSchema>;

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const form = useForm<CreateValues>({
    resolver: zodResolver(adminCreateUserSchema),
    defaultValues: { isAdmin: false },
  });
  const create = useMutation({
    mutationFn: (v: CreateValues) =>
      apiRequest('/api/application/users', {
        method: 'POST',
        // An empty password field means "email them a set-password link".
        body: { ...v, password: v.password || undefined },
        schema: adminUserSchema,
      }),
    onSuccess: async (user, v) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success(
        v.password
          ? `Created ${user.username}.`
          : `Created ${user.username} and emailed a set-password link.`,
      );
      onClose();
      await navigate({ to: '/admin/users/$userId', params: { userId: String(user.id) } });
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New user</DialogTitle>
          <DialogDescription>
            Leave the password empty to email the user a link to choose their own.
          </DialogDescription>
        </DialogHeader>
        <form
          id="create-user"
          className="grid gap-4"
          noValidate
          onSubmit={form.handleSubmit(
            (v) => create.mutate(v),
            () => undefined,
          )}
        >
          <FormField label="Email" error={form.formState.errors.email?.message}>
            <Input type="email" autoFocus {...form.register('email')} />
          </FormField>
          <FormField label="Username" error={form.formState.errors.username?.message}>
            <Input {...form.register('username')} />
          </FormField>
          <FormField label="Password (optional)" error={form.formState.errors.password?.message}>
            <Input
              type="password"
              autoComplete="new-password"
              {...form.register('password', { setValueAs: (v: string) => v || undefined })}
            />
          </FormField>
          <Controller
            control={form.control}
            name="isAdmin"
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="isAdmin"
                  checked={field.value}
                  onCheckedChange={(c) => field.onChange(c === true)}
                />
                <Label htmlFor="isAdmin">Administrator</Label>
              </div>
            )}
          />
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-user" disabled={create.isPending}>
            {create.isPending && <Loader2 className="animate-spin" />} Create user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
